import { useState, useRef, useEffect, useContext } from 'react';
import { instanaReportError, instanaCustomEvent } from '@bizzabo/instana-api';

import {
    MAX_CONCURRENT_STREAMS,
    SESSION_GRACE_TIME,
    getTimeToSessionEndWithDelayMs,
    hashStringTo32bitInt,
    isMobile,
} from '../sessionBroadcastUtils';
import { viewController } from '../LiveBroadcast';
import { AgendaContext } from '../AgendaContextProvider';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { postAttendeeHearbeat } from '../../ApiUtil';

const HTTP_HEATBEAT_INTERVAL = 5000;

const supportAttendeeHearbeat = async(params, payload) => {
    try {
        await postAttendeeHearbeat(params, payload);
    } catch (e) {
        const instanaError = `[Attendee hearbeat]: ${e.message}, error: ${JSON.stringify(e)}`;
        instanaReportError({ error: instanaError });
    }
};

export const useConcurrentUsersNotifier = ({
    session,
    userEmail,
    isFeatureEnabled,
    uiController,
    virtualSessionDetails,
    socket,
    ticketsSelectionData
}) => {
    const [isConcurrentUsers, setIsConcurrentUsers] = useState(false);
    const concurrentStreams = useRef(new Map());

    const isMobileDevice = isMobile();
    const { event } = useContext(AgendaContext);
    const timeZone = event?.['time-zone-id'];
    const accountId = event?.['account-id'];
    const { endDate, endMinute, id: sessionId, eventId, startTime, endTime } = session || {};
    const { isThirdPartyCookiesSupported, streamId, bizzaboStreamId } = virtualSessionDetails || {};
    let socketStreamId = streamId;
    if (!streamId && bizzaboStreamId) {
        socketStreamId = `${bizzaboStreamId}_MAIN`;
    } else if (!streamId && sessionId) {
        socketStreamId = `${sessionId}`;
    }

    const loadSessionPageForExternalSession = FeatureToggleUtils.isFeatureEnabled('external-session-join-leave');

    const attendeeHeartbeatPollingId = useRef(null);

    useEffect(() => {
        if (
            (uiController === viewController.SIMULIVE ||
            uiController === viewController.EMBEDDED ||
            uiController === viewController.WEBCASTING ||
            (uiController === viewController.EXTERNAL && loadSessionPageForExternalSession) ||
            (uiController === viewController.ZOOM && loadSessionPageForExternalSession) ||
            uiController === viewController.INTERACTIVE_VIDEO_PROVIDER ||
            (uiController === viewController.NEWROW_IFRAME && !isMobileDevice && isThirdPartyCookiesSupported) ||
            uiController === viewController.VIDEO_PROVIDER) &&
            isFeatureEnabled
        ) {
            connectAndSubscribe();
        }
    }, [uiController, isMobileDevice, isFeatureEnabled, isThirdPartyCookiesSupported]);

    useEffect(() => () => {
        stopAttendeeHeartbeatPolling();
        socket?.close?.();
    }, []);

    const stopAttendeeHeartbeatPolling = () => {
        if (attendeeHeartbeatPollingId.current) {
            clearInterval(attendeeHeartbeatPollingId.current);
            attendeeHeartbeatPollingId.current = null;
        }
    };

    const connectAndSubscribe = async() => {
        try {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] CONNECTING',
                meta: { email: userEmail },
            });
            const clientFacing = true;
            const entityId = hashStringTo32bitInt(userEmail);
            const payload = {
                streamId: socketStreamId,
                sessionId,
                ticketId: ticketsSelectionData?.tickets?.[0]?.id,
                startTime,
                endTime
            };
            await socket?.connect(accountId, eventId, 'agenda', {
                allowedRestarts: 0,
                skipCurrentSessionRetries: true,
                secondsToInitResetCount: 20,
                fallback: () => fallback(entityId, payload),
                onConnected: stopAttendeeHeartbeatPolling,
            }, clientFacing);

            socket?.onConcurrentUsersUpdate({
                cb: onSubscribeHandler,
                entityId,
                ...(
                    socketStreamId && {
                        data: payload
                    }
                )
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            const instanaError = `[Avoid Concurrent User]: ${e.message}, error: ${JSON.stringify(e)}`;
            instanaReportError({error: instanaError});

            if (!e.message.includes('Connection closed')) {
                instanaCustomEvent({
                    eventName: '[Avoid Concurrent User] CONNECT PROBLEMS',
                    meta: { email: userEmail, error: e.message },
                });
            }
        }
    };

    const onSubscribeHandler = ({ deviceId, type, timestamp }) => {
        if (type === 'INIT') {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] INIT',
                meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            concurrentStreams.current?.set('me', { deviceId, timestamp });
            return;
        }

        if (type === 'JOIN') {
            const me = concurrentStreams.current?.get('me');
            if (me?.timestamp <= timestamp && me?.deviceId !== deviceId) {
                instanaCustomEvent({
                    eventName: '[Avoid Concurrent User] JOIN',
                    meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
                });
                concurrentStreams.current?.set(deviceId, timestamp);
            }
        }

        if (type === 'LEAVE') {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] LEAVE',
                meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            concurrentStreams.current?.delete(deviceId);
        }

        if (concurrentStreams.current?.size > MAX_CONCURRENT_STREAMS) {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] Detected concurrent user',
                meta: { size: concurrentStreams.current?.size, curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            setIsConcurrentUsers(true);
            socket?.close?.();
            concurrentStreams.current?.clear();
        }
    };

    const fallback = (connectionId, payload) => {
        const timeToSessionEnd = getTimeToSessionEndWithDelayMs(
            { endDate, endMinute },
            SESSION_GRACE_TIME,
            timeZone
        );

        if (timeToSessionEnd > 0) {
            if (!attendeeHeartbeatPollingId.current) {
                attendeeHeartbeatPollingId.current = setInterval(() => {
                    supportAttendeeHearbeat({accountId, eventId, connectionId}, payload);
                }, HTTP_HEATBEAT_INTERVAL);
            }
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] RECONNECT',
                meta: { email: userEmail },
            });
            connectAndSubscribe();
        } else {
            stopAttendeeHeartbeatPolling();
        }
    };

    const connectConcurrentUsersNotifier = () => {
        setIsConcurrentUsers(false);
        connectAndSubscribe();
    };

    const disconnectConcurrentUsersNotifier = () => {
        stopAttendeeHeartbeatPolling();
        concurrentStreams.current?.clear();
        socket?.close?.();
    };

    return {
        isConcurrentUsers,
        connectConcurrentUsersNotifier,
        disconnectConcurrentUsersNotifier,
    };
};
